import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { NgApexchartsModule } from "ng-apexcharts";
import {
  ApexAxisChartSeries,
  ApexChart,
  ApexDataLabels,
  ApexPlotOptions,
  ApexXAxis,
  ApexYAxis,
  ApexFill,
  ApexTooltip,
  ApexResponsive,
  ApexLegend,
} from "ng-apexcharts";

export type ChartOptions = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  xaxis: ApexXAxis;
  yaxis: ApexYAxis;
  fill: ApexFill;
  tooltip: ApexTooltip;
  responsive: ApexResponsive[];
  legend: ApexLegend;
  colors: string[];
};

@Component({
  selector: "risk-summary-chart",
  standalone: true,
  imports: [CommonModule, NgApexchartsModule],
  template: `
    <div #chartContainer class="w-full dashboard-alignment-fixed-height">
      <div *ngIf="isNoData" class="risk-summary-empty-state">
        <strong>No active risk flags</strong>
        <span>Seed or API data will populate severity bars here.</span>
      </div>
      <apx-chart
        *ngIf="!isNoData && chartOptions"
        [series]="chartOptions.series"
        [chart]="chartOptions.chart"
        [plotOptions]="chartOptions.plotOptions"
        [dataLabels]="chartOptions.dataLabels"
        [xaxis]="chartOptions.xaxis"
        [yaxis]="chartOptions.yaxis"
        [colors]="chartOptions.colors"
        [tooltip]="chartOptions.tooltip"
        [legend]="chartOptions.legend"
      ></apx-chart>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        width: 100%;
        min-width: 0;
      }

      .dashboard-alignment-fixed-height {
        min-height: 184px;
        overflow: hidden;
      }

      .risk-summary-empty-state {
        min-height: 176px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        border: 1px solid rgba(226, 232, 240, 0.9);
        border-radius: 16px;
        background: #f8fafc;
        text-align: center;
        color: #64748b;
      }

      .risk-summary-empty-state strong {
        color: #334155;
        font-size: 13px;
        font-weight: 800;
      }

      .risk-summary-empty-state span {
        margin-top: 4px;
        font-size: 12px;
        font-weight: 600;
      }
    `,
  ],
})
export class RiskSummaryChartComponent implements OnChanges, AfterViewInit {
  @Output() dataEvent = new EventEmitter<any>();
  @Input() chartData: { label: string; count: number }[] = [];
  @ViewChild("chartContainer") chartContainer!: ElementRef<HTMLDivElement>;

  public chartOptions: Partial<ChartOptions> | any;
  public isNoData = true;

  colorMap: Record<string, string> = {
    critical: "#D61409",
    high: "#F86522",
    medium: "#FFBD40",
    low: "#00B25A",
  };

  defaultColor = "#9CA3AF";

  ngAfterViewInit() {
    this.updateChart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["chartData"]) {
      this.updateChart();
    }
  }

private updateChart(): void {
  const normalizedData = Array.isArray(this.chartData)
    ? this.chartData
        .map((item) => ({
          label: item?.label || "Unknown",
          count: Number.isFinite(Number(item?.count)) ? Number(item.count) : 0,
        }))
        .filter((item) => item.label)
    : [];

  this.isNoData = !normalizedData.length || normalizedData.every((item) => item.count === 0);
  if (this.isNoData) {
    this.chartOptions = null;
    return;
  }

  const labels = normalizedData.map((d) => d.label);
  const values = normalizedData.map((d) => d.count);
  const colors = normalizedData.map(
    (d) => this.colorMap[d.label.toLowerCase()] || this.defaultColor
  );

  const chartHeight = Math.max(184, Math.min(240, normalizedData.length * 46));
  const maxCount = Math.max(...values, 1);
  const paddedMax = Math.ceil(maxCount * 1.18);

  this.chartOptions = {
    series: [
      {
        name: "Count",
        data: values,
      },
    ],
    chart: {
      type: "bar",
      height: chartHeight,
      toolbar: { show: false },
      // FIX: Add margin to the bottom to prevent category label overlap
      margin: {
          top: 0,
          right: 0,
          bottom: 30,
          left: 0
      },
      events: {
        dataPointSelection: (_event: any, _chartContext: any, config: any) => {
                        const item = normalizedData[config.dataPointIndex];

          if (item) {
            this.dataEvent.emit(item);
          }
        },
      },
    },
    plotOptions: {
      bar: {
        horizontal: true,
        borderRadius: 5,
        distributed: true,
        barHeight: "56%",
        dataLabels: {
          position: "top",
        },
        colors: {
          ranges: [],
          backgroundBarColors: [],
        },
      },
    },
    dataLabels: {
      enabled: true,
      textAnchor: "start",
      offsetX: 10,
      style: {
        colors: ["#111"],
        fontWeight: 700,
        fontSize: "12px",
      },
      formatter: (val: number) => val.toString(),
    },
    xaxis: {
      categories: labels,
      min: 0,
      max: paddedMax,
      labels: {
        style: {
          fontSize: "11px",
          fontWeight: 700,
          colors: "#111",
        },
      },
      axisBorder: {
        show: true, 
        color: "#333",
        height: 2,
      },
      axisTicks: {
        show: true, 
        color: "#333",
      },
    },
    yaxis: {
      labels: {
        style: {
          fontSize: "12px",
          fontWeight: 700,
          colors: "#111",
        },
      },
    },
    colors,
    fill: {
      type: "solid",
      opacity: 1,
    },
    tooltip: {
      enabled: false,
    },
    legend: {
      show: false,
    },
    theme: {
      mode: "light",
      monochrome: {
        enabled: false,
      },
    },
  };
}

}
